/**
 * Self-service access-request contract types (PRO-0580 / ADR-036).
 *
 * Single source of truth shared by BOTH tiers — the API (`apps/api`, building the
 * response + boundary validation) and the SPA (`apps/web`, the request dialog +
 * approver queue). Pure types + constants only (no `pg`), so the SPA imports the
 * runtime constants (`REQUESTABLE_ROLES`, the reverse map) from this module
 * directly and stays browser-safe.
 *
 * PII posture (SEC-8): the requester justification (`reason`) and approver
 * `decisionReason` live only on the row + the gated detail surface — NEVER in
 * `audit_log` or logs (TD-5).
 */
import type { Capability, StaffRole } from './auth';

/** Access-request lifecycle. Mirrors the `access_request.status` CHECK (migration 0060). */
export type AccessRequestStatus = 'pending' | 'approved' | 'denied' | 'withdrawn' | 'auto_closed';

/**
 * Inc-1 requestable allow-list (TD-3) — the authoritative EoP guard for this route.
 * A STRICT SUBSET of the assignable roles that ALSO excludes `L6_PLATFORM_ADMIN`,
 * `CEO`/`COO` and every safeguarding/special-category role. All Inc-1 requestable
 * roles are SCHOOL-SCOPED (so a request always carries a `schoolId`, SF-002).
 * Widened only by DPO ratification — never speculatively.
 */
export const REQUESTABLE_ROLES = [
  'L1_SENDER',
  'L2_APPROVER',
  'COMPLAINT_HANDLER',
  'ATR_REQUESTER',
  // PRO-3100 / ADR-018 PRO-3100 amendment — a missing issue or action owner can ask for
  // access (migration 0226 widens the 0060 CHECK). Both are school-scoped, provision-only.
  'OPS_SITE_CONTRIBUTOR',
  'OPS_ACTION_OWNER',
] as const;

export type RequestableRole = (typeof REQUESTABLE_ROLES)[number];

export function isRequestableRole(value: unknown): value is RequestableRole {
  return typeof value === 'string' && (REQUESTABLE_ROLES as readonly string[]).includes(value);
}

/** Max length of the requester justification + approver denial reason (row-only). */
export const ACCESS_REQUEST_REASON_MAX = 2000;

/**
 * Capability → self-requestable role reverse map (CF-SEC-001). Maps a denied
 * capability to the role a user could self-request to obtain it. A capability whose
 * ONLY conferring role is appointment-only (e.g. `complaint_oversight_read` →
 * `OPS_DIRECTOR`, `author_school_templates` → `PRINCIPAL`/`OPS_DIRECTOR`, every
 * `atr_*` reviewer/decision capability, `manage_*`, `insights_*`) is DELIBERATELY
 * ABSENT — `requestableRoleForCapability` returns `null`, so the SPA shows the
 * email/contact fallback instead of offering a request `REQUESTABLE_ROLES` would
 * reject. Unit-invariant: every value ∈ `REQUESTABLE_ROLES`.
 */
export const CAPABILITY_REQUESTABLE_ROLE: Partial<Record<Capability, RequestableRole>> = {
  compose_send: 'L1_SENDER',
  approve_broadcast: 'L2_APPROVER',
  reject_broadcast: 'L2_APPROVER',
  complaint_record: 'COMPLAINT_HANDLER',
  complaint_manage: 'COMPLAINT_HANDLER',
  complaint_history_export: 'COMPLAINT_HANDLER',
  complaint_evidence_manage: 'COMPLAINT_HANDLER',
  // `atr_submit` is also held by the appointment-only PRINCIPAL, but the
  // self-requestable conferring role is ATR_REQUESTER.
  atr_submit: 'ATR_REQUESTER',
  ops_issue_raise: 'OPS_SITE_CONTRIBUTOR',
  ops_issue_read: 'OPS_SITE_CONTRIBUTOR',
  ops_issue_update: 'OPS_SITE_CONTRIBUTOR',
  ops_action_manage: 'OPS_SITE_CONTRIBUTOR',
  ops_action_own: 'OPS_ACTION_OWNER',
};

/** Reverse-map lookup: the self-requestable role for a capability, or null (appointment-only). */
export function requestableRoleForCapability(capability: Capability): RequestableRole | null {
  return CAPABILITY_REQUESTABLE_ROLE[capability] ?? null;
}

// ---------------------------------------------------------------------------
// Wire contracts
// ---------------------------------------------------------------------------

/** `POST /access-requests` request body (SF-001: idempotencyKey is correlation-only). */
export interface CreateAccessRequestBody {
  readonly requestedRole: RequestableRole;
  readonly schoolId: string;
  readonly reason?: string;
  readonly idempotencyKey: string;
}

export interface CreateAccessRequestResponse {
  readonly reference: string;
  readonly status: 'pending' | 'auto_closed';
}

/** `GET /access-requests/schools` item — id + name ONLY (non-PII directory, TD-9). */
export interface RequestableSchool {
  readonly schoolId: string;
  readonly name: string;
}

export interface RequestableSchoolsResponse {
  readonly schools: readonly RequestableSchool[];
}

/**
 * Approver-queue summary. The free-text justification (`reason`) is surfaced here
 * for the informed decision (the queue read is capability-gated) alongside the
 * capabilities the grant would confer. `conferredCapabilities` is computed at the
 * API layer from the `CAPABILITIES` map.
 */
export interface AccessRequestSummary {
  readonly accessRequestId: string;
  readonly reference: string;
  readonly requestedRole: StaffRole;
  readonly schoolId: string;
  readonly requesterOid: string;
  readonly requesterDisplayName: string | null;
  readonly status: AccessRequestStatus;
  readonly reason: string | null;
  readonly createdAt: string;
  readonly conferredCapabilities: readonly Capability[];
}

export interface AccessRequestQueueResponse {
  readonly items: readonly AccessRequestSummary[];
  readonly nextCursor: string | null;
}

/** `POST /access-requests/:id/approve|deny` response — terminal state echo. */
export interface AccessRequestDecisionResponse {
  readonly reference: string;
  readonly status: AccessRequestStatus;
}

/** `POST /access-requests/:id/deny` body — reason required (stored in-row, TD-5). */
export interface DenyAccessRequestBody {
  readonly reason: string;
}

// ---------------------------------------------------------------------------
// Service inputs + row interfaces (DB-4, no `any`)
// ---------------------------------------------------------------------------

export interface SubmitAccessRequestInput {
  readonly requestedRole: RequestableRole;
  readonly schoolId: string;
  readonly reason: string | null;
  readonly requesterOid: string;
  readonly requesterDisplayName: string | null;
  readonly idempotencyKey: string;
  /** The caller already holds the requested role at this scope → short-circuit auto_close (SF-001). */
  readonly alreadyHeld: boolean;
}

export interface ListPendingParams {
  readonly limit: number;
  readonly cursor?: string;
}

/**
 * Queue item the service returns (pre-enrichment). The API route adds
 * `conferredCapabilities` (from the `CAPABILITIES` map) to build the wire summary.
 */
export interface AccessRequestQueueItem {
  readonly accessRequestId: string;
  readonly reference: string;
  readonly requestedRole: StaffRole;
  readonly schoolId: string;
  readonly requesterOid: string;
  readonly requesterDisplayName: string | null;
  readonly status: AccessRequestStatus;
  readonly reason: string | null;
  readonly createdAt: string;
}

/** Full record for the approve/deny/detail path (justification included). */
export interface AccessRequestRecord {
  readonly accessRequestId: string;
  readonly reference: string;
  readonly requesterOid: string;
  readonly requesterDisplayName: string | null;
  readonly requestedRole: StaffRole;
  readonly schoolId: string;
  readonly status: AccessRequestStatus;
  readonly reason: string | null;
  readonly resultingAssignmentId: string | null;
}

/** Pending row for the notifier digest sweep (metadata + requester identity). */
export interface PendingAccessRequestDigestRow {
  readonly accessRequestId: string;
  readonly reference: string;
  readonly requestedRole: StaffRole;
  readonly schoolId: string;
  readonly requesterOid: string;
  readonly requesterDisplayName: string | null;
  readonly createdAt: string;
}

/** DB row shapes (explicit; no `any`). */
export interface AccessRequestQueueRow {
  readonly access_request_id: string;
  readonly reference: string;
  readonly requested_role: StaffRole;
  readonly school_id: string;
  readonly requester_oid: string;
  readonly requester_display_name: string | null;
  readonly status: AccessRequestStatus;
  readonly reason: string | null;
  readonly created_at: Date;
}

export interface AccessRequestRecordRow {
  readonly access_request_id: string;
  readonly reference: string;
  readonly requester_oid: string;
  readonly requester_display_name: string | null;
  readonly requested_role: StaffRole;
  readonly school_id: string;
  readonly status: AccessRequestStatus;
  readonly reason: string | null;
  readonly resulting_assignment_id: string | null;
}
